import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useParams } from 'react-router-dom';
import useGetLists from '../hooks/useGetLists';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import useEditList from '../hooks/useEditList';

type ListRenameModalProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
};

const ListRenameModal = ({ open, setOpen }: ListRenameModalProps) => {
	const { t } = useTranslation();
	const { id_list } = useParams();
	const { lists } = useGetLists();

	const { editList } = useEditList();

	const listSchema = z.object({
		name: z.string().min(1, { message: t('common.error.required') }),
	});

	const list = lists.find((list) => list.id === parseInt(id_list as string));

	const form = useForm<z.infer<typeof listSchema>>({
		resolver: zodResolver(listSchema),
		defaultValues: {
			name: list?.name || '',
		},
	});

	if (!list) return null;

	const onSubmit = (values: z.infer<typeof listSchema>) => {
		editList({ id: list.id, name: values.name });
		setOpen(false);
	};

	return (
		<Dialog open={open} modal={true} onOpenChange={(open) => setOpen(open)}>
			<DialogContent className="max-w-[95%] sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>{t('list.modal.rename.title')}</DialogTitle>
					<DialogDescription>
						{t('list.modal.rename.description')}
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form id="rename" onSubmit={form.handleSubmit(onSubmit)}>
						<div className="w-full">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<Input
											id="name"
											label={t('list.form.name')}
											placeholder={t(
												'list.form.placeholder'
											)}
											className="w-full"
											{...field}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</form>
				</Form>
				<DialogFooter className="gap-3 pt-4">
					<Button variant="outline" onClick={() => setOpen(false)}>
						{t('common.cancel')}
					</Button>
					<Button type="submit" form="rename">
						{t('common.save')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default ListRenameModal;
